import { useState } from "react";
import { useCreate, useTranslate } from "@refinedev/core";
import { ActionBar, Button, CheckboxField, Field, FormCard, PasswordField, SectionHeader } from "@fawkes/ui";

// Create a user — single-step form over POST /api/users. username (login_name) is required + unique
// (409 → duplicate message); a password is optional (omit it and use the reset-link action so the user
// sets their own). Mirrors the Jmix User create form minus role assignment (deferred).
export function UserCreate({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const t = useTranslate();
  const { mutate: create, isLoading } = useCreate();

  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function submit() {
    if (!username.trim()) {
      setError(t("userCreateView.error.usernameRequired"));
      return;
    }
    setError(null);
    create(
      {
        resource: "users",
        values: {
          username: username.trim(),
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: email || undefined,
          employeeId: employeeId || undefined,
          active,
          password: password || undefined,
        },
      },
      {
        onSuccess: () => onDone(),
        onError: (err) => {
          const status = (err as { statusCode?: number })?.statusCode;
          setError(status === 409 ? t("userCreateView.error.duplicate") : t("userCreateView.error.generic"));
        },
      },
    );
  }

  return (
    <FormCard>
      <SectionHeader>{t("userCreateView.title")}</SectionHeader>
      {error && <p className="fawkes-error" role="alert">{error}</p>}
      <Field label={t("userCreateView.field.username")} value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
      <Field label={t("userDetailView.field.firstName")} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      <Field label={t("userDetailView.field.lastName")} value={lastName} onChange={(e) => setLastName(e.target.value)} />
      <Field label={t("userDetailView.field.email")} type="email" invalidEmailMessage={t("validatie.emailadres")}
        value={email} onChange={(e) => setEmail(e.target.value)} />
      <Field label={t("userDetailView.field.employeeId")} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
      <PasswordField
        label={t("userCreateView.field.password")}
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        revealLabel={t("login.showPassword", undefined, "Wachtwoord tonen")}
        hideLabel={t("login.hidePassword", undefined, "Wachtwoord verbergen")}
      />
      <p className="fawkes-muted">{t("userCreateView.field.passwordHelp")}</p>
      <CheckboxField label={t("userDetailView.field.active")} checked={active} onChange={(e) => setActive(e.target.checked)} />
      <ActionBar>
        <Button variant="primary" onClick={submit} disabled={isLoading}>{t("userCreateView.submit")}</Button>
        <Button variant="secondary" onClick={onCancel}>{t("common.cancel")}</Button>
      </ActionBar>
    </FormCard>
  );
}
